import React, { useReducer, useEffect, useCallback, useRef } from 'react'

import { useAuth0 } from '@auth0/auth0-react'

import { setUserTimezone } from '@/insights/common/timezone'
import { isInsightAllowed as isInsightAllowedRule } from '@/insights/insightAccess'
import {
  exchangeTokenWithRetry,
  getStoredTokenWithValidation,
  saveToken,
  clearToken,
  requestAccess,
  NoInsightsAccessError,
} from '@/services/authService'

import { registerNeedsHardLoginCallback, unregisterNeedsHardLoginCallback } from './apiService'
import type { AuthContextValue } from './auth-types'
import {
  AuthContext,
  authReducer,
  detectSudo,
  extractInsightsFromToken,
  extractPermissionsFromToken,
  extractUserFromAuth0Token,
  initialState,
} from './authContext'

// --- Provider ---

const fullScreenStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '100vh',
  flexDirection: 'column',
  gap: '16px',
  fontSize: '16px',
  padding: '0 24px',
  textAlign: 'center',
}

const screenButtonStyle: React.CSSProperties = {
  marginTop: '8px',
  padding: '8px 24px',
  cursor: 'pointer',
  borderRadius: '6px',
  border: '1px solid #ccc',
  background: '#f5f5f5',
  fontSize: '14px',
}

/**
 * Shown when sign-in succeeded but the account has no active Insights profile.
 * Renders before any provider is up, hence the inline styles.
 */
const RequestAccessScreen: React.FC<{
  onRequest: () => Promise<void>
  onSignOut: () => void
}> = ({ onRequest, onSignOut }) => {
  const [requestState, setRequestState] = React.useState<'idle' | 'sending' | 'sent' | 'failed'>('idle')

  const handleRequest = async () => {
    setRequestState('sending')
    try {
      await onRequest()
      setRequestState('sent')
    } catch (err) {
      console.error('Access request failed:', err)
      setRequestState('failed')
    }
  }

  return (
    <div style={fullScreenStyle}>
      <span style={{ fontSize: '20px', fontWeight: 600 }}>No access yet</span>
      <span>Your sign-in worked, but this account does not have access to Cooltrax Insights yet.</span>
      {requestState === 'sent' ? (
        <span>Request sent — an administrator will set you up and let you know.</span>
      ) : (
        <>
          {requestState === 'failed' && <span>The request could not be sent — try again shortly.</span>}
          <button onClick={() => void handleRequest()} disabled={requestState === 'sending'} style={screenButtonStyle}>
            {requestState === 'sending' ? 'Sending…' : 'Request access'}
          </button>
        </>
      )}
      <button onClick={onSignOut} style={screenButtonStyle}>
        Sign in with a different account
      </button>
    </div>
  )
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const initRef = useRef(false)

  const {
    isAuthenticated: isAuth0Authenticated,
    isLoading: auth0Loading,
    error: auth0Error,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    logout: auth0Logout,
    user: auth0UserProfile,
  } = useAuth0()

  // Register 401 callback
  useEffect(() => {
    registerNeedsHardLoginCallback(() => dispatch({ type: 'SET_NEEDS_HARD_LOGIN', needsHardLogin: true }))
    return () => unregisterNeedsHardLoginCallback()
  }, [])

  // Publish the user's profile timezone so non-React code (insight `load`
  // functions) resolves the same zone the components render in.
  useEffect(() => {
    setUserTimezone(state.user?.timezone)
  }, [state.user?.timezone])

  // Main initialization effect
  useEffect(() => {
    if (auth0Loading) return
    if (initRef.current) return

    // Not authenticated with Auth0 → redirect to login. Unless Auth0 itself
    // refused (e.g. the post-login Action denied this connection): redirecting
    // again would just loop — fall through and show the denial instead.
    if (!isAuth0Authenticated) {
      if (auth0Error) return
      const returnTo = window.location.pathname + window.location.search + window.location.hash
      loginWithRedirect({
        appState: { returnTo },
      }).catch((err) => console.error('Auth0 redirect failed:', err))
      return
    }

    // Auth0 is authenticated → exchange token
    initRef.current = true

    const initialize = async () => {
      try {
        // 1. Check for a valid stored app token
        let appToken = getStoredTokenWithValidation()

        if (!appToken) {
          // 2. Get Auth0 access token and exchange
          const accessToken = await getAccessTokenSilently()
          const result = await exchangeTokenWithRetry(accessToken, 3)
          appToken = result.token
          saveToken(appToken)
        }

        // 3. Extract permissions from app token
        const permissions = extractPermissionsFromToken(appToken)
        const insights = extractInsightsFromToken(appToken)

        // 4. Extract user from Auth0 access token
        const accessToken = await getAccessTokenSilently()
        let profileImage = ''
        try {
          const idClaims = await getIdTokenClaims()
          profileImage = idClaims?.picture || ''
        } catch {
          // fallback
        }
        if (!profileImage && auth0UserProfile?.picture) {
          profileImage = auth0UserProfile.picture
        }

        const auth0User = extractUserFromAuth0Token(accessToken, profileImage)
        if (!auth0User) {
          throw new Error('Unable to extract user data from token')
        }

        // 5. Detect sudo
        const { user, originalUser } = detectSudo(auth0User, appToken)

        dispatch({ type: 'SET_AUTHENTICATED', token: appToken, user, originalUser, permissions, insights })
      } catch (err) {
        console.error('Auth initialization failed:', err)

        // A clean sign-in with no Insights profile is not an error — it is a
        // person who needs an admin. Offer the request-access flow.
        if (err instanceof NoInsightsAccessError) {
          dispatch({ type: 'SET_NO_ACCESS' })
          return
        }

        // If the refresh token is missing (e.g. existing session from before
        // useRefreshTokens was enabled), clear stale Auth0 cache and re-login
        // so a fresh session with a refresh token is established.
        const msg = err instanceof Error ? err.message : String(err)
        if (msg.includes('Missing Refresh Token')) {
          Object.keys(localStorage).forEach((key) => {
            if (key.startsWith('@@auth0spajs@@')) localStorage.removeItem(key)
          })
          initRef.current = false
          const returnTo = window.location.pathname + window.location.search + window.location.hash
          loginWithRedirect({ appState: { returnTo } }).catch((redirectErr) =>
            console.error('Re-login redirect failed:', redirectErr)
          )
          return
        }

        dispatch({ type: 'SET_INITIALIZED_ERROR', error: msg })
      }
    }

    initialize()
  }, [
    auth0Loading,
    isAuth0Authenticated,
    auth0Error,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect,
    auth0UserProfile,
  ])

  // --- Actions ---

  const setNeedsHardLogin = useCallback((v: boolean) => {
    dispatch({ type: 'SET_NEEDS_HARD_LOGIN', needsHardLogin: v })
  }, [])

  const hasPermission = useCallback(
    (p: string): boolean => {
      const perms = state.permissions
      if (p.endsWith('*')) {
        const prefix = p.slice(0, -1)
        return perms.some((perm) => perm.startsWith(prefix))
      }
      return perms.includes(p)
    },
    [state.permissions]
  )

  const isInsightAllowed = useCallback((slug: string) => isInsightAllowedRule(state.insights, slug), [state.insights])

  const hasAnyPermission = useCallback(
    (ps: string): boolean => {
      const perms = state.permissions
      return ps.split(/\s+/).some((p) => perms.includes(p))
    },
    [state.permissions]
  )

  const logout = useCallback(
    async (options?: { logoutParams?: { returnTo?: string } }) => {
      try {
        clearToken()

        // Clear Auth0 caches
        Object.keys(localStorage).forEach((key) => {
          if (
            key.startsWith('@@auth0spajs@@') ||
            key.startsWith('auth_') ||
            key.startsWith('jellyfish_') ||
            key.startsWith('sdk_')
          ) {
            localStorage.removeItem(key)
          }
        })
        Object.keys(sessionStorage).forEach((key) => {
          if (key.startsWith('auth_') || key.startsWith('@@auth0spajs@@')) {
            sessionStorage.removeItem(key)
          }
        })

        dispatch({ type: 'CLEAR' })

        await auth0Logout({
          logoutParams: {
            returnTo: options?.logoutParams?.returnTo || window.location.origin,
          },
        })
      } catch (err) {
        console.error('Logout failed:', err)
        Object.keys(localStorage).forEach((key) => {
          if (key.startsWith('@@auth0spajs@@')) localStorage.removeItem(key)
        })
        localStorage.removeItem('MEGA_API_TOKEN')
        window.location.href = window.location.origin
      }
    },
    [auth0Logout]
  )

  const refreshToken = useCallback(async () => {
    try {
      const accessToken = await getAccessTokenSilently()
      const result = await exchangeTokenWithRetry(accessToken, 3)
      saveToken(result.token)

      const permissions = extractPermissionsFromToken(result.token)
      const insights = extractInsightsFromToken(result.token)

      // Re-extract user info
      let profileImage = ''
      try {
        const idClaims = await getIdTokenClaims()
        profileImage = idClaims?.picture || ''
      } catch {
        // ignore
      }
      if (!profileImage && auth0UserProfile?.picture) {
        profileImage = auth0UserProfile.picture
      }

      const auth0User = extractUserFromAuth0Token(accessToken, profileImage)
      if (!auth0User) throw new Error('Unable to extract user')

      const { user, originalUser } = detectSudo(auth0User, result.token)
      dispatch({ type: 'SET_AUTHENTICATED', token: result.token, user, originalUser, permissions, insights })
    } catch (err) {
      console.error('Token refresh failed:', err)
      dispatch({ type: 'CLEAR' })
    }
  }, [getAccessTokenSilently, getIdTokenClaims, auth0UserProfile])

  const value: AuthContextValue = {
    token: state.token,
    user: state.user,
    originalUser: state.originalUser,
    permissions: state.permissions,
    insights: state.insights,
    isAuthenticated: !!state.token,
    isLoading: state.isLoading,
    isInitialized: state.isInitialized,
    needsHardLogin: state.needsHardLogin,
    hasPermission,
    hasAnyPermission,
    isInsightAllowed,
    setNeedsHardLogin,
    logout,
    refreshToken,
  }

  // Auth0 itself refused the sign-in (e.g. the post-login Action denied this
  // connection). Without this branch the not-authenticated path would redirect
  // straight back to login and loop forever.
  if (!isAuth0Authenticated && !auth0Loading && auth0Error) {
    return (
      <AuthContext.Provider value={value}>
        <div style={fullScreenStyle}>
          <span style={{ fontSize: '20px', fontWeight: 600 }}>Sign-in refused</span>
          <span>{auth0Error.message || 'Your sign-in was refused.'}</span>
          <span>If you believe you should have access, contact your administrator.</span>
          <button
            onClick={() => {
              window.location.href = '/'
            }}
            style={screenButtonStyle}
          >
            Try again
          </button>
        </div>
      </AuthContext.Provider>
    )
  }

  // While loading, show nothing (ConfigProvider shows the fullscreen loader)
  if (!state.isInitialized) {
    return <AuthContext.Provider value={value}>{null}</AuthContext.Provider>
  }

  // Signed in, but no active Insights profile — offer the request-access flow.
  if (state.noAccess) {
    return (
      <AuthContext.Provider value={value}>
        <RequestAccessScreen
          onRequest={async () => {
            const accessToken = await getAccessTokenSilently()
            await requestAccess(accessToken)
          }}
          onSignOut={() => void logout()}
        />
      </AuthContext.Provider>
    )
  }

  // Backend down / token exchange failed
  if (state.error && !state.token) {
    return (
      <AuthContext.Provider value={value}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            flexDirection: 'column',
            gap: '16px',
            fontSize: '16px',
          }}
        >
          <span>An error occurred during login. Try logging in again.</span>
          <span>If the problem persists, contact support.</span>
          <button
            onClick={() => {
              window.location.href = '/'
            }}
            style={{
              marginTop: '8px',
              padding: '8px 24px',
              cursor: 'pointer',
              borderRadius: '6px',
              border: '1px solid #ccc',
              background: '#f5f5f5',
              fontSize: '14px',
            }}
          >
            Try again
          </button>
        </div>
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
